import {Dropdown, TextArea, TextInput, Toggle} from '@/components/atoms'
import {SectionTitle} from '@/components/molecules'
import {AccordionCustom as Accordion} from '@/components/molecules/AccordionCustom'
import {AccordionItemCustom as AccordionItem} from '@/components/molecules/AccordionItemCustom'
import {PageContainer} from '@/components/organisms'
import {SegmentModal} from '@/containers/Promotions/components/SegmentModal/SegmentModal'
import {useAllSegments} from '@/hooks/useAllSegments'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {getSegmentsDatafromSegmentsIds} from '@/utils/commons'
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants'
import {SegmentTypes} from '@/utils/constants/promotionConstants'
import {useCallback, useMemo, useState} from 'react'

const ProductInformation = ({
  baseDataFields,
  baseDataFormData,
  categoryListData,
  handleFormChange,
  additionaInfoFields,
  errors,
}) => {
  const languages = useFeatureFlagLanguages()
  const {allSegments} = useAllSegments()
  const updatedAt = baseDataFormData?.updatedDateTime ?? new Date()
  const formattedUpdatedAt = new Date(updatedAt).toISOString().split('T')[0]
  // State for segment modal
  const [showSegmentModal, setShowSegmentModal] = useState(false)

  // segementsList uses category list (dropdowns/categories → { categoryId, category }) for dropdown display
  const segmentsList =
    categoryListData?.length &&
    categoryListData?.map(item => ({
      displayText: item.category,
      id: item.categoryId,
      isBadge: false,
    }))

  // We are assuming that the status list is static and not fetched from an API.
  // If it is dynamic, you would need to fetch it from the API and set it in the List.
  const statusList = []

  const renderBaseDataField = field => {
    if (field.type === 'dropdown') {
      // Define isSegment and segments variables
      const isSegment = field.key === DROPDOWN_TYPES.Segments
      const segmentIds = baseDataFormData?.baseData?.segments || []
      const hasSegments = segmentIds?.length > 0

      const options =
        isSegment && segmentsList?.length
          ? segmentsList
          : field.key === 'status' && statusList?.length
            ? statusList
            : field?.options?.map(option => ({displayText: option, id: option})) // Fallback to static options

      const value = isSegment
        ? segmentIds
            ?.filter(seg => seg)
            .map(seg => ({
              displayText: typeof seg === 'string' ? seg : seg.name || seg.segmentName,
              id: typeof seg === 'string' ? seg : seg.id,
            }))
        : [{displayText: baseDataFormData.baseData.status}]

      const selectedSegments = getSegmentsDatafromSegmentsIds(
        Array.isArray(segmentIds) ? segmentIds : segmentIds ? [segmentIds].flat() : [],
        allSegments
      )
      // Get segment names for display
      const segmentNames =
        selectedSegments &&
        selectedSegments.map(seg => (typeof seg === 'string' ? seg : seg.name || seg.segmentName)).filter(Boolean)

      let placeholderText = isSegment && hasSegments ? segmentNames.join(', ') : field.placeholder // Get placeholder text and truncate if too long to prevent overlap with icons

      const fullPlaceholderText = placeholderText
      // Truncate if too long
      if (placeholderText?.length > 25) {
        placeholderText = `${placeholderText.substring(0, 25)}...`
      }

      // For segments, wrap in a div to capture clicks
      if (isSegment) {
        return (
          <div
            key={field.key}
            role="button"
            tabIndex={field?.isDisabled ? -1 : 0}
            onClick={() => !field.isDisabled && setShowSegmentModal(true)}
            onKeyDown={e => {
              if (field.isDisabled) return

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setShowSegmentModal(true)
              }
            }}
            style={{cursor: field.isDisabled ? 'default' : 'pointer'}}
          >
            <Dropdown
              className="text-sm"
              childList={[]} // Empty list since we're using modal
              isDark={true}
              label={field.label}
              value={value}
              isJsonWrap={false}
              disabled={field.isDisabled}
              selection={field.isMultiple ? 'multiple' : 'single'}
              placeholder={placeholderText || ''}
              title={fullPlaceholderText}
              updateValue={() => {}} // No-op since we're using modal
              style={{pointerEvents: 'none'}} // Prevent dropdown from opening
            />
          </div>
        )
      }

      return (
        <Dropdown
          key={field.key}
          className="text-sm"
          childList={options}
          isDark={true}
          label={field.label}
          value={value}
          isJsonWrap={false}
          disabled={field.isDisabled}
          selection={field.isMultiple ? 'multiple' : 'single'}
          placeholder={placeholderText || ''}
          title={fullPlaceholderText}
          updateValue={value => {
            const updatedValue = isSegment ? value.map(v => v.displayText) : value[0].displayText
            handleFormChange({baseData: {...baseDataFormData?.baseData, [field.key]: updatedValue}})
          }}
        />
      )
    }

    if (field.type === 'toggle') {
      return (
        <Toggle
          key={field.key}
          uiSchema={{size: 'medium', isDark: true, label: field.label}}
          value={baseDataFormData?.baseData?.[field.key]}
          error={errors?.[field.key]}
          updateValue={value => handleFormChange({baseData: {...baseDataFormData?.baseData, [field.key]: value}})}
        />
      )
    }

    if (['allowedInMultiplesOf', 'suggestedSellingPrice'].includes(field.key)) {
      return (
        <TextInput
          key={field.key}
          className="text-sm"
          isDark={true}
          label={field.label}
          placeholderText={field.placeholder}
          value={baseDataFormData?.additionalInfo?.[field.key]}
          error={errors?.[field.key]}
          disabled={field.isDisabled}
          type="number"
          id={field.key}
          onChange={value =>
            handleFormChange({additionalInfo: {...baseDataFormData?.additionalInfo, [field.key]: value}})
          }
        />
      )
    }

    return (
      <TextInput
        key={field.key}
        className="text-sm"
        isDark={true}
        label={field.label}
        placeholderText={field.placeholder}
        value={baseDataFormData?.baseData?.[field.key]}
        error={errors?.[field.key]}
        disabled={field.isDisabled}
        id={field.key} // Add an id to associate the label with the input
        onChange={value => handleFormChange({baseData: {...baseDataFormData?.baseData, [field.key]: value}})}
      />
    )
  }

  const renderAdditionInfoField = field => {
    return (
      <TextInput
        key={field.key}
        className="text-sm"
        isDark={true}
        label={field.label}
        placeholderText={field.placeholder}
        value={baseDataFormData?.additionalInfo?.[field.key]}
        error={errors?.[field.key]}
        disabled={field.isDisabled}
        type={field.type}
        id={field.key} // Add an id to associate the label with the input
        onChange={value =>
          handleFormChange({additionalInfo: {...baseDataFormData?.additionalInfo, [field.key]: value}})
        }
      />
    )
  }

  const handleSubmit = useCallback(
    segments => {
      const newSegmentIds = segments.map(seg => seg.segmentId)

      handleFormChange({baseData: {...baseDataFormData?.baseData, segments: newSegmentIds}})

      setShowSegmentModal(false)
    },
    [baseDataFormData, handleFormChange]
  )

  const handleClose = useCallback(() => {
    setShowSegmentModal(false)
  }, [])

  const existingSelections = useMemo(() => {
    const segments = baseDataFormData?.baseData?.segments || []
    return segments.map(segmentId => {
      const segmentData = getSegmentsDatafromSegmentsIds([segmentId], allSegments)?.[0]
      return {
        segmentId: segmentId,
        segmentGroupId: '',
        segmentName: segmentData?.name || segmentId,
      }
    })
  }, [baseDataFormData?.baseData?.segments, allSegments])

  return (
    <>
      <PageContainer data-testid="product-base-data-product-information-content">
        <div className="grid grid-cols-1">
          <p className="updated-by">Updated: {formattedUpdatedAt}, Admin</p>
          <SectionTitle label="Product Information" />
          <div className="sc-border grid grid-cols-4 gap-4 pb-5">
            {baseDataFields
              .filter(field => !field.isMultilingual)
              .map(field => (
                <div key={field.key} className={`col-4 pt-5 ${field?.className ? field.className : ''}`}>
                  {renderBaseDataField(field)}
                </div>
              ))}
          </div>
          <div className="grid grid-cols-4 gap-4 pb-5">
            {languages.map((item, idx) => {
              const language = item.code
              const languageName = item.language
              return (
                <div key={language} className="col-4 pt-5">
                  <h5 className="heading pb-3 font-bold">{languageName}</h5>
                  {baseDataFields
                    .filter(field => field.isMultilingual && !field.isIdShown)
                    .map(field => (
                      <div className="col-4 pt-5" key={field.key}>
                        <TextInput
                          key={`${field.key}-${language}`}
                          className="text-sm"
                          isDark={true}
                          disabled={!field?.isDisabled}
                          label={field.label}
                          placeholderText={field.placeholder}
                          value={baseDataFormData?.baseData?.[field.key]?.[language]}
                          error={errors?.[field.key]?.[language]}
                          id={`${field.key}-${language}`} // Add an id to associate the label with the input
                          onChange={value =>
                            handleFormChange({
                              baseData: {
                                ...baseDataFormData?.baseData,
                                [field.key]: {
                                  ...baseDataFormData?.baseData?.[field.key],
                                  [language]: value,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                </div>
              )
            })}
          </div>

          <div className="flex  items-center gap-5 pb-5">
            {languages.map((item, idx) => {
              const language = item.code
              const languageName = item.language
              const field = baseDataFields.find(f => f.isIdShown && f.key === 'sizeByLocale')
              return (
                <div key={language} className=" max-w-374 flex w-full items-center gap-5 pt-5">
                  <div className="max-w-374 w-full" key={field?.key}>
                    <TextInput
                      key={`${field?.key}-${language}`}
                      className="text-sm"
                      isDark={true}
                      required={field?.isRequired}
                      disabled={field?.isDisabled}
                      label={field?.label}
                      placeholderText={field?.placeholder}
                      value={baseDataFormData?.baseData?.[field?.key]?.[language]}
                      // error={errors?.[field?.key]?.[language]}
                      id={`${field?.key}-${language}`} // Add an id to associate the label with the input
                      onChange={value =>
                        handleFormChange({
                          baseData: {
                            ...baseDataFormData?.baseData,
                            [field.key]: {
                              ...baseDataFormData?.baseData?.[field?.key],
                              [language]: value,
                            },
                          },
                        })
                      }
                      maxLength={255}
                    />
                  </div>
                </div>
              )
            })}
            <div className="max-w-374 w-full pt-5">
              <TextInput
                className="text-sm"
                isDark={true}
                disabled={false}
                label={'Display Product Size Id'}
                placeholderText={'Add Display Product Size Id'}
                value={baseDataFormData?.baseData?.['sizeId']}
                error={errors?.['sizeId']}
                id={`sizeByLocale`} // Add an id to associate the label with the input
                onChange={value =>
                  handleFormChange({
                    baseData: {
                      ...baseDataFormData.baseData,
                      sizeId: value,
                    },
                  })
                }
                required={true}
                maxLength={255}
              />
            </div>
          </div>
          <div className="flex  items-center gap-5 pb-5">
            {languages.map((item, idx) => {
              const language = item.code
              const languageName = item.language
              const field = baseDataFields.find(f => f.isIdShown && f.key === 'productTypeByLocale')
              return (
                <div key={language} className=" max-w-374 flex w-full items-center gap-5 pt-5">
                  <div className="max-w-374 w-full" key={field?.key}>
                    <TextInput
                      key={`${field?.key}-${language}`}
                      className="text-sm"
                      isDark={true}
                      disabled={field?.isDisabled}
                      label={field?.label}
                      placeholderText={field?.placeholder}
                      value={baseDataFormData?.baseData?.[field?.key]?.[language]}
                      // error={errors?.[field?.key]?.[language]}
                      id={`${field?.key}-${language}`} // Add an id to associate the label with the input
                      onChange={value =>
                        handleFormChange({
                          baseData: {
                            ...baseDataFormData?.baseData,
                            [field.key]: {
                              ...baseDataFormData?.baseData?.[field?.key],
                              [language]: value,
                            },
                          },
                        })
                      }
                      required={field?.isRequired}
                      maxLength={255}
                    />
                  </div>
                </div>
              )
            })}
            <div className="max-w-374 w-full pt-5">
              <TextInput
                className="text-sm"
                isDark={true}
                disabled={false}
                label={'Display Product Type Id'}
                placeholderText={'Add Display Product Type Id'}
                value={baseDataFormData?.baseData?.['productTypeId']}
                error={errors?.['productTypeId']}
                id={`productTypeId`} // Add an id to associate the label with the input
                onChange={value =>
                  handleFormChange({
                    baseData: {
                      ...baseDataFormData.baseData,
                      productTypeId: value,
                    },
                  })
                }
                required={true}
                maxLength={255}
              />
            </div>
          </div>
        </div>
      </PageContainer>
      <div className="basedata-accordion-content-wrapper">
        <Accordion
          isDark={true}
          content={
            <AccordionItem
              isDark={true}
              expanded
              size="large"
              label="Returns & Deposits"
              presentation="test"
              content={
                <>
                  <div className="product-basedata">
                    <div className="grid grid-cols-3 gap-4 pb-5">
                      <div className="col-4 toggle-alignment gap-5 pt-5">
                        <Toggle
                          uiSchema={{size: 'medium', isDark: true, label: 'Is Returnable'}}
                          value={baseDataFormData?.baseData?.isReturnable}
                          error={errors?.isReturnable} // Display error message if it exists
                          updateValue={value =>
                            handleFormChange({baseData: {...baseDataFormData?.baseData, isReturnable: value}})
                          }
                          disabled={true}
                        />
                        <Toggle
                          uiSchema={{size: 'medium', isDark: true, label: 'Has Deposit'}}
                          value={baseDataFormData?.baseData?.isDepositable}
                          error={errors?.isDepositable} // Display error message if it exists
                          updateValue={value =>
                            handleFormChange({baseData: {...baseDataFormData?.baseData, isDepositable: value}})
                          }
                        />
                      </div>
                    </div>
                    <div className="sc-border grid grid-cols-3 gap-4 pb-5">
                      <div className="col-4 toggle-alignment gap-2 pt-5">
                        <TextArea
                          uiSchema={{
                            isDark: true,
                            helperText: 'Helper text',
                            placeholder: 'Type Here',
                            showToolbar: false,
                            htmlMode: false,
                          }}
                          label="Description"
                          value={baseDataFormData?.baseData?.description}
                          error={errors?.description} // Display error message if it exists
                          disabled={true}
                          updateValue={value =>
                            handleFormChange({baseData: {...baseDataFormData?.baseData, description: value}})
                          }
                        />
                      </div>
                      <div className="col-4 toggle-alignment gap-2 pt-5">
                        <TextArea
                          uiSchema={{
                            isDark: true,
                            helperText: 'Helper text',
                            placeholder: 'Type Here',
                            showToolbar: false,
                            htmlMode: false,
                          }}
                          label="Description Business"
                          value={baseDataFormData?.baseData?.businessDescription || ''}
                          error={errors?.businessDescription}
                          updateValue={value =>
                            handleFormChange({
                              baseData: {...baseDataFormData?.baseData, businessDescription: value},
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </>
              }
            />
          }
        />
      </div>

      <div className="basedata-accordion-content-wrapper">
        <Accordion
          isDark={true}
          content={
            <AccordionItem
              isDark
              size="large"
              label="Additional Information"
              presentation="test"
              content={
                <div className="sc-border grid grid-cols-4 gap-4 pb-5">
                  {additionaInfoFields.map(field => (
                    <div key={field?.key} className={`col-4 pt-5 ${field?.className ? field?.className : ''}`}>
                      {renderAdditionInfoField(field)}
                    </div>
                  ))}
                </div>
              }
            />
          }
        />
      </div>

      {/* Segment Modal */}
      {showSegmentModal && (
        <SegmentModal
          show={showSegmentModal}
          type={SegmentTypes.SEGMENT_LIST}
          onClose={handleClose}
          onSubmit={handleSubmit}
          existingSelections={existingSelections}
        />
      )}
    </>
  )
}

export default ProductInformation
